import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Calculator, 
  DollarSign, 
  CheckCircle, 
  Clock,
  Phone,
  Percent,
  Calendar,
  Shield,
  FileText
} from 'lucide-react';

const FinancingPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Roofing Financing Options | Carolina Trophy Roofs | Easy Payment Plans</title>
        <meta name="description" content="Flexible roofing financing options with competitive rates and easy approval. Make your roof replacement affordable with Carolina Trophy Roofs financing programs in Eastern NC." />
        <meta name="keywords" content="roofing financing, roof replacement financing, easy payment plans, roofing loans, Rocky Mount financing, Greenville roofing finance" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/financing" />
        
        <meta property="og:title" content="Roofing Financing Options | Carolina Trophy Roofs" />
        <meta property="og:description" content="Affordable financing solutions for your roofing project. Competitive rates and flexible payment options." />
        <meta property="og:url" content="https://www.carolinatrophyroofs.com/financing" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white py-20 md:py-28">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&h=1080&fit=crop&auto=format&q=75" 
            alt="Home financing calculator" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-brand-safety-orange text-white px-4 py-2 text-sm font-semibold">
              FLEXIBLE PAYMENT OPTIONS
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold">
              Roofing Financing Made Simple
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto">
              Don't let budget concerns delay your roof replacement. Explore our flexible financing options with competitive rates and easy approval.
            </p>
            
            {/* Dealer Number Info in Hero */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-center mb-3">
                <FileText className="mr-3 h-6 w-6 text-brand-trophy-gold" />
                <span className="text-brand-trophy-gold font-semibold text-lg">Required for Application</span>
              </div>
              <div className="text-center">
                <p className="text-white mb-2">Use Our Dealer Number:</p>
                <div className="inline-flex items-center bg-brand-safety-orange text-white px-6 py-3 rounded-lg font-bold text-2xl">
                  391677
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://slfportal.com/orange/#/dealerLogin" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                  <Calculator className="mr-2 h-5 w-5" />
                  Apply for Financing
                </Button>
              </a>
              <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                <Phone className="mr-2 h-5 w-5" />
                Call (252) 302-2003
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Application Instructions */}
          <div className="mb-16">
            <Card className="bg-gradient-to-br from-brand-trophy-gold/10 to-yellow-50 border-2 border-brand-trophy-gold/30">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold mb-6 text-brand-charcoal-gray text-center">
                  How to Apply for Financing
                </h2>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 text-brand-charcoal-gray">Step-by-Step Process:</h3>
                    <ol className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <span className="bg-brand-safety-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                        Click "Apply for Financing" to access the portal
                      </li>
                      <li className="flex items-start">
                        <span className="bg-brand-safety-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                        Enter dealer number <strong>391677</strong> when prompted
                      </li>
                      <li className="flex items-start">
                        <span className="bg-brand-safety-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                        Complete the online application form
                      </li>
                      <li className="flex items-start">
                        <span className="bg-brand-safety-orange text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                        Receive approval decision in minutes
                      </li>
                    </ol>
                  </div>
                  <div className="text-center">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                      <FileText className="h-12 w-12 text-brand-safety-orange mx-auto mb-4" />
                      <h4 className="text-lg font-bold text-brand-charcoal-gray mb-2">Dealer Number</h4>
                      <div className="bg-brand-safety-orange text-white px-6 py-3 rounded-lg font-bold text-2xl mb-3">
                        391677
                      </div>
                      <p className="text-sm text-gray-600">Required for all financing applications</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Financing Options */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-charcoal-gray">
              Financing Options Available
            </h2>
            <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Choose the financing solution that works best for your budget and timeline
            </p>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {[
                {
                  icon: CreditCard,
                  title: "Personal Financing",
                  subtitle: "Quick & Easy",
                  features: [
                    "Same-day approval available",
                    "Competitive interest rates",
                    "Terms up to 12 years",
                    "No prepayment penalties",
                    "Fixed monthly payments"
                  ],
                  highlight: "As low as 5.99% APR*",
                  buttonText: "Apply Now",
                  featured: true
                },
                {
                  icon: Percent,
                  title: "Traditional Financing",
                  subtitle: "Bank & Credit Union Options",
                  features: [
                    "Home equity lines of credit",
                    "Personal loans from your bank",
                    "Cash-out refinancing",
                    "Construction loans",
                    "We work with your lender"
                  ],
                  highlight: "Multiple Options Available",
                  buttonText: "Discuss Options"
                }
              ].map((option, index) => (
                <Card key={index} className={`relative ${
                  option.featured ? 'border-2 border-brand-trophy-gold bg-gradient-to-br from-yellow-50/50 to-white' : 'border-2 border-gray-200'
                } hover:shadow-lg transition-all duration-300`}>
                  {option.featured && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-brand-trophy-gold text-brand-charcoal-gray font-semibold">
                      RECOMMENDED
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-brand-trophy-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <option.icon className="h-8 w-8 text-brand-trophy-gold" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-brand-charcoal-gray">
                      {option.title}
                    </CardTitle>
                    <p className="text-brand-safety-orange font-semibold">{option.subtitle}</p>
                    <div className="text-lg font-bold text-brand-trophy-gold mt-2">
                      {option.highlight}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      {option.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {option.title === "Traditional Financing" ? (
                      <Link to="/contact">
                        <Button className={`w-full mt-6 ${
                          option.featured 
                            ? 'bg-brand-safety-orange hover:bg-brand-safety-orange/90' 
                            : 'bg-brand-charcoal-gray hover:bg-brand-charcoal-gray/90'
                        } text-white`}>
                          {option.buttonText}
                        </Button>
                      </Link>
                    ) : (
                      <a href="https://slfportal.com/orange/#/dealerLogin" target="_blank" rel="noopener noreferrer">
                        <Button className={`w-full mt-6 ${
                          option.featured 
                            ? 'bg-brand-safety-orange hover:bg-brand-safety-orange/90' 
                            : 'bg-brand-charcoal-gray hover:bg-brand-charcoal-gray/90'
                        } text-white`}>
                          {option.buttonText}
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              Why Choose Our Financing?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Clock,
                  title: "Quick Approval",
                  description: "Get approved in minutes with our streamlined application process."
                },
                {
                  icon: Shield,
                  title: "Secure Process",
                  description: "Your financial information is protected with bank-level security."
                },
                {
                  icon: Calendar,
                  title: "Flexible Terms",
                  description: "Choose payment terms that fit your budget and lifestyle."
                },
                {
                  icon: FileText,
                  title: "No Hidden Fees",
                  description: "Transparent pricing with no surprise charges or hidden fees."
                }
              ].map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-brand-safety-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="h-8 w-8 text-brand-safety-orange" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-brand-charcoal-gray">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              How Financing Works
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Get Your Estimate",
                  description: "Schedule a free inspection and receive a detailed estimate for your roofing project."
                },
                {
                  step: "2", 
                  title: "Apply for Financing",
                  description: "Complete our simple online application or apply over the phone with our team."
                },
                {
                  step: "3",
                  title: "Start Your Project",
                  description: "Once approved, we'll schedule your roofing project and begin protecting your home."
                }
              ].map((step, index) => (
                <Card key={index} className="text-center border-2 border-gray-200 hover:border-brand-trophy-gold/30 transition-all duration-300">
                  <CardHeader>
                    <div className="w-16 h-16 bg-brand-safety-orange rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">
                      {step.step}
                    </div>
                    <CardTitle className="text-xl text-brand-charcoal-gray">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">
              Financing FAQ
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  question: "What credit score do I need?",
                  answer: "We work with a range of credit scores. Even if you have less-than-perfect credit, we may have financing options available for you."
                },
                {
                  question: "How quickly can I get approved?",
                  answer: "Many applications are approved within minutes. We'll let you know your approval status right away."
                },
                {
                  question: "Are there any prepayment penalties?",
                  answer: "No, most of our financing options have no prepayment penalties. Pay off your loan early without extra fees."
                },
                {
                  question: "Can I finance the entire project?",
                  answer: "Yes, you can finance up to 100% of your roofing project cost, including materials and labor."
                },
                {
                  question: "What documents do I need?",
                  answer: "Typically just basic information like income verification and identification. We keep the paperwork simple."
                },
                {
                  question: "Is there a minimum project amount?",
                  answer: "Financing is available for projects of various sizes. Contact us to discuss your specific project needs."
                }
              ].map((faq, index) => (
                <Card key={index} className="border-2 border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-brand-charcoal-gray">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-gradient-to-r from-brand-charcoal-gray to-gray-800 text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold mb-4 text-brand-trophy-gold">
                Ready to Get Started?
              </h2>
              <p className="text-xl mb-6 max-w-2xl mx-auto text-gray-200">
                Don't let financing concerns delay your roof replacement. Apply for financing today and protect your home tomorrow.
              </p>
              
              {/* Dealer Number Reminder */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-8 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-2">
                  <FileText className="mr-2 h-5 w-5 text-brand-trophy-gold" />
                  <span className="text-brand-trophy-gold font-semibold">Remember: Use Dealer Number</span>
                </div>
                <div className="bg-brand-safety-orange text-white px-4 py-2 rounded-lg font-bold text-lg">
                  391677
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://slfportal.com/orange/#/dealerLogin" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white font-semibold px-8 py-4">
                    <Calculator className="mr-2 h-5 w-5" />
                    Apply for Financing
                  </Button>
                </a>
                <Button size="lg" variant="outline" className="border-brand-trophy-gold text-brand-trophy-gold hover:bg-brand-trophy-gold/10">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 302-2003
                </Button>
              </div>
              <div className="mt-8 text-sm text-gray-300">
                <p>* Subject to credit approval. Terms and conditions apply. APR varies based on creditworthiness.</p>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default FinancingPage;